import { useState, useEffect } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import Sidebar from "../../../components/sidebar/Sidebar";
import { getSelectStyles } from "../../../utils/selectStyles";
import "./CreateAccountPage.css";

export default function CreateAccountPage() {

  const [created, setCreated] = useState(false);
  const [error, setError]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [referralWarning, setReferralWarning] = useState(null);

  const [accounts, setAccounts]     = useState([]);
  const [isReseller, setIsReseller] = useState(false);
  const [isReferral, setIsReferral] = useState(false);
  const [refererID, setRefererID]   = useState("");
  const [refererOption, setRefererOption] = useState(null);

  const [account, setAccount] = useState({
    username: "",
    email: "",
    telegram: "",
    telegramID: "",
    discord: "",
    discordID: "",
    discordPrivateChannelID: "",
    createdDate: null,
  });

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/account`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then(res => setAccounts(res.data || []))
      .catch(() => {});
  }, []);

  const loadRefererOptions = (inputValue, callback) => {
    const filtered = accounts.filter(a =>
      a.username.toLowerCase().includes(inputValue.toLowerCase()) ||
      a.email?.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered.map(a => ({ value: a._id, label: `${a.username}${a.email ? ` (${a.email})` : ''}` })));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'createdDate') {
      setAccount(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setAccount(prev => ({ ...prev, [name]: value }));
    }
  };

  const formatDateForInput = (unixMs) =>
    unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isReseller && isReferral && !refererID) { setError("Please select who referred this account, or uncheck the referral option"); return; }
    setError(null);
    setReferralWarning(null);
    setCreated(false);
    setSubmitting(true);

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/account/create`,
        {
          username: account.username,
          email: account.email,
          telegram: account.telegram,
          telegramID: account.telegramID,
          discord: account.discord,
          discordID: account.discordID,
          discordPrivateChannelID: account.discordPrivateChannelID,
          accountType: isReseller ? 'reseller' : 'customer',
          createdDate: account.createdDate + "",
          updatedDate: account.createdDate + "",
        },
        { headers }
      );

      setCreated(true);

      if (!isReseller && isReferral && refererID) {
        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/referral/create`,
            { referer: refererID, refered: res.data.id, referDate: account.createdDate + "", notes: "" },
            { headers }
          );
        } catch (refErr) {
          setReferralWarning(refErr.response?.data?.message || "Account was created, but the referral could not be recorded. You may need to add it manually.");
        }
      }

      setAccount({
        username: "", email: "", telegram: "", telegramID: "", discord: "",
        discordID: "", discordPrivateChannelID: "", createdDate: null,
      });
      setIsReseller(false);
      setIsReferral(false);
      setRefererID("");
      setRefererOption(null);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Sidebar title="Create Account" />

      <div className="form-page">
        <div className="form-panel">

          {/* Header */}
          <div className="form-panel-header">
            <div className="form-panel-icon">
              <i className="bi bi-person-plus-fill" />
            </div>
            <div>
              <div className="form-panel-title">Create New Account</div>
              <div className="form-panel-subtitle">Fill in the details below to register a new account</div>
            </div>
          </div>

          {/* Body */}
          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              {/* ── Identity ── */}
              <div className="form-section-label">Identity</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-person-badge" />
                      Username
                      <span className="required-star">*</span>
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="username"
                      value={account.username}
                      onChange={handleChange}
                      placeholder="e.g. johndoe"
                      required
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-envelope" />
                      Email
                    </label>
                    <input
                      className="form-input"
                      type="email"
                      name="email"
                      value={account.email}
                      onChange={handleChange}
                      placeholder="e.g. john@email.com"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* ── Social ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Social</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-send" />
                      Telegram
                    </label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">@</span>
                      <input
                        className="form-input"
                        type="text"
                        name="telegram"
                        value={account.telegram}
                        onChange={handleChange}
                        placeholder="username"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-hash" />
                      Telegram ID
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="telegramID"
                      value={account.telegramID}
                      onChange={handleChange}
                      placeholder="e.g. 123456789"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-discord" />
                      Discord Username
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="discord"
                      value={account.discord}
                      onChange={handleChange}
                      placeholder="e.g. johndoe"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-hash" />
                      Discord ID
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="discordID"
                      value={account.discordID}
                      onChange={handleChange}
                      placeholder="e.g. 123456789012345678"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-chat-left-dots" />
                      Private Channel ID
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="discordPrivateChannelID"
                      value={account.discordPrivateChannelID}
                      onChange={handleChange}
                      placeholder="e.g. 987654321098765432"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* ── Dates ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Dates</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-calendar-plus" />
                      Created Date
                      <span className="required-star">*</span>
                    </label>
                    <input
                      className="form-input"
                      type="date"
                      name="createdDate"
                      value={formatDateForInput(account.createdDate)}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* ── Account type ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Account Type</div>
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-field-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isReseller}
                      onChange={e => {
                        setIsReseller(e.target.checked);
                        // Resellers have no referrals, so clear any selection made before the tick.
                        if (e.target.checked) { setIsReferral(false); setRefererID(""); setRefererOption(null); }
                      }}
                    />
                    This is a reseller account
                  </label>
                  {isReseller && (
                    <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--color-muted)' }}>
                      Resellers buy credits in bulk rather than holding subscriptions, so they get no
                      subscriptions, referrals or points. Log their credit purchases under Resellers.
                    </div>
                  )}
                </div>
              </div>

              {/* ── Referral ── */}
              {!isReseller && (
              <>
              <div className="form-section-label" style={{ marginTop: 8 }}>Referral</div>
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-field-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isReferral}
                      onChange={e => { setIsReferral(e.target.checked); if (!e.target.checked) { setRefererID(""); setRefererOption(null); } }}
                    />
                    This account is a result of a referral
                  </label>
                </div>
                {isReferral && (
                  <div className="col-md-6">
                    <div className="form-field">
                      <label className="form-field-label"><i className="bi bi-person-fill" />Referred By <span className="required-star">*</span></label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={loadRefererOptions}
                        value={refererOption}
                        onChange={opt => { setRefererOption(opt); setRefererID(opt?.value || ""); }}
                        placeholder="Search account…"
                        isClearable
                        styles={getSelectStyles()}
                      />
                    </div>
                  </div>
                )}
              </div>
              </>
              )}

              {/* Feedback */}
              {created && (
                <div className="form-banner success">
                  <i className="bi bi-check-circle-fill" />
                  Account created successfully! You can create another one above.
                </div>
              )}
              {referralWarning && (
                <div className="form-banner error">
                  <i className="bi bi-exclamation-triangle-fill" />
                  {referralWarning}
                </div>
              )}
              {error && (
                <div className="form-banner error">
                  <i className="bi bi-exclamation-triangle-fill" />
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-form-cancel"
                  onClick={() => window.history.back()}
                >
                  <i className="bi bi-x-lg" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-form-submit"
                  disabled={submitting}
                >
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-person-plus-fill'}`} />
                  {submitting ? 'Creating…' : 'Create Account'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
}
